/**
 * Lógica pura de la sección Prospectos Web.
 *
 * Los leads del cotizador llegan como documentos anidados (visitante / vehiculo /
 * ubicacion / captura / cotizaciones). La tabla necesita filas planas y el modal necesita
 * el documento entero, así que la traducción entre ambos vive acá, separada de React y de
 * Firestore para poder probarla sin montar nada.
 */

/** Estados del ciclo de vida del lead, con su chip. Sin gestión asociada todavía. */
const ESTADO_CHIPS = {
    NUEVO: 'bg-sky-50 text-sky-700 border border-sky-200',
    CONTACTADO: 'bg-amber-50 text-amber-700 border border-amber-200',
    CONVERTIDO: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    PERDIDO: 'bg-slate-100 text-slate-500 border border-slate-200'
};

const CHIP_DESCONOCIDO = 'bg-slate-50 text-slate-600 border border-slate-200';

export function estadoProspectoWeb(estado) {
    return ESTADO_CHIPS[String(estado || '').toUpperCase()] || CHIP_DESCONOCIDO;
}

/**
 * De dónde vino el lead, en lenguaje de negocio.
 *
 * `origen` se guarda como enum (`COTIZADOR_AUTOS`) porque es lo que escribe el cotizador
 * y lo que se consulta; la pantalla no tiene por qué mostrar eso. Un origen que todavía
 * no está mapeado se vuelve legible igual, para que agregar un cotizador nuevo no saque
 * un enum crudo a la cara del usuario.
 */
const ORIGEN_LABELS = {
    COTIZADOR_AUTOS: 'Seguro para autos'
};

export function labelOrigen(origen) {
    const clave = String(origen || '').trim().toUpperCase();
    if (!clave) return 'Cotizador web';
    if (ORIGEN_LABELS[clave]) return ORIGEN_LABELS[clave];

    const legible = clave.replace(/_/g, ' ').toLowerCase();
    return legible.charAt(0).toUpperCase() + legible.slice(1);
}

/**
 * Género en palabras.
 *
 * El cotizador no le pregunta el género al visitante: siempre cotiza como Masculino, así
 * que este dato describe la cotización, no a la persona. Por eso la ausencia de valor cae
 * en Masculino en vez de en un guion — es lo que efectivamente se usó para cotizar.
 */
const GENERO_LABELS = {
    M: 'Masculino',
    MASCULINO: 'Masculino',
    F: 'Femenino',
    FEMENINO: 'Femenino'
};

export function labelGenero(genero) {
    return GENERO_LABELS[String(genero || '').trim().toUpperCase()] || 'Masculino';
}

/** Una línea con el auto cotizado, salteando lo que no vino. */
export function resumenVehiculo(vehiculo) {
    if (!vehiculo) return '-';

    const partes = [vehiculo.marca, vehiculo.modelo, vehiculo.anio]
        .filter((parte) => parte !== null && parte !== undefined && String(parte).trim() !== '');

    return partes.length ? partes.join(' ').toUpperCase() : '-';
}

/**
 * La cotización más barata de las que se le mostraron al visitante: es el número que
 * define si el lead vale la pena llamar, así que va en la tabla.
 */
export function mejorCotizacion(cotizaciones) {
    const conPremio = (cotizaciones || []).filter((c) => typeof c?.premioMensual === 'number');
    if (!conPremio.length) return null;

    return conPremio.reduce((menor, actual) => (actual.premioMensual < menor.premioMensual ? actual : menor));
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Fecha en dd/mm/yyyy.
 *
 * Las fechas 'YYYY-MM-DD' se leen como texto, no con `new Date`: el constructor las
 * interpreta como medianoche UTC, y en Argentina (UTC-3) eso muestra el día anterior.
 * Un cumpleaños corrido un día es un error que nadie nota hasta que alguien reclama.
 */
export function formatDate(value) {
    if (!value) return '';

    const isoMatch = typeof value === 'string' && ISO_DATE.exec(value.trim());
    if (isoMatch) {
        const [, anio, mes, dia] = isoMatch;
        return `${dia}/${mes}/${anio}`;
    }

    const fecha = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(fecha.getTime())) return '';

    const dia = String(fecha.getDate()).padStart(2, '0');
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    return `${dia}/${mes}/${fecha.getFullYear()}`;
}

/** Firestore devuelve Timestamp; el resto del código quiere Date. */
function toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    return new Date(value);
}

/**
 * Aplana un lead en la fila que consume la tabla.
 * Conserva el documento completo en `raw` para que abrir el ojo no dispare otra consulta.
 */
export function toRow(lead) {
    const visitante = lead.visitante || {};
    const vehiculo = lead.vehiculo || {};
    const ubicacion = lead.ubicacion || {};
    const captura = lead.captura || {};
    const cotizaciones = lead.cotizaciones || [];
    const mejor = mejorCotizacion(cotizaciones);

    const nombreCompleto = [visitante.nombre, visitante.apellido].filter(Boolean).join(' ').trim();

    return {
        id: lead.id,
        displayNombre: nombreCompleto ? nombreCompleto.toUpperCase() : 'SIN NOMBRE',
        email: visitante.email || '',
        telefono: visitante.telefono || '',
        documento: visitante.dni || '',
        vehiculo: resumenVehiculo(vehiculo),
        anio: vehiculo.anio ?? null,
        localidad: ubicacion.localidad || '',
        provincia: ubicacion.provincia || '',
        dispositivo: captura.dispositivo || '',
        origen: lead.origen || '',
        estado: lead.estado || 'NUEVO',
        fechaCreacion: toDate(lead.fechaCreacion),
        cantidadCotizaciones: cotizaciones.length,
        mejorPremio: mejor ? mejor.premioMensual : null,
        mejorCompania: mejor ? mejor.compania : '',
        raw: lead
    };
}

function isEmpty(value) {
    return value === null || value === undefined || value === '';
}

/**
 * Ordena por una columna.
 *
 * Los vacíos van siempre al final, en las dos direcciones: un lead sin prima cotizada no
 * es "el más barato", y dejarlo primero al ordenar ascendente esconde los que sí importan.
 */
export function sortRows(rows, sortConfig) {
    if (!sortConfig?.key) return [...rows];

    const { key, direction } = sortConfig;
    // El repo usa 'ascending'/'descending' (lo que espera SortableHeader para la flecha);
    // aceptamos también la forma corta para no romper a quien la pase.
    const factor = String(direction || '').startsWith('desc') ? -1 : 1;

    return [...rows].sort((a, b) => {
        const left = a[key];
        const right = b[key];

        if (isEmpty(left) && isEmpty(right)) return 0;
        if (isEmpty(left)) return 1;
        if (isEmpty(right)) return -1;

        if (left instanceof Date && right instanceof Date) return (left - right) * factor;
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;

        return String(left).localeCompare(String(right), 'es') * factor;
    });
}

const CAMPOS_BUSCABLES = ['displayNombre', 'email', 'telefono', 'documento', 'vehiculo', 'localidad', 'mejorCompania'];

/** Búsqueda libre sobre los campos que alguien tipearía para encontrar un lead. */
export function filterRows(rows, term) {
    const needle = String(term || '').trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
        CAMPOS_BUSCABLES.some((campo) => String(row[campo] || '').toLowerCase().includes(needle))
    );
}

const MONEY_FORMATTER = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });

/** Montos en pesos. El cero es un monto; la ausencia de monto es un guion. */
export function formatMoney(amount) {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '-';
    return `$ ${MONEY_FORMATTER.format(amount)}`;
}

const PERCENT_FORMATTER = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

/**
 * Franquicia de una cobertura.
 *
 * Las compañías la expresan de dos maneras: como porcentaje de la suma asegurada —lo más
 * común en autos— o como importe fijo. Por eso son dos campos y no uno: reinterpretar el
 * mismo campo como porcentaje haría que un importe viejo de 900000 se leyera como 900000%.
 *
 * `0` y "sin dato" tampoco son lo mismo: `0` es una cobertura sin franquicia —un argumento
 * de venta— y la ausencia es que el cotizador no informó el valor.
 */
export function formatFranquicia(cotizacion) {
    const { franquiciaPorcentaje, franquicia } = cotizacion || {};

    if (typeof franquiciaPorcentaje === 'number' && !Number.isNaN(franquiciaPorcentaje)) {
        return franquiciaPorcentaje === 0 ? 'Sin franquicia' : `${PERCENT_FORMATTER.format(franquiciaPorcentaje)}%`;
    }
    if (typeof franquicia === 'number' && !Number.isNaN(franquicia)) {
        return franquicia === 0 ? 'Sin franquicia' : formatMoney(franquicia);
    }
    return '—';
}

/* ─────────────────────── ANCHO DE COLUMNAS (redimensionable) ─────────────────────── */

export const MIN_COLUMN_WIDTH = 70;
export const MAX_COLUMN_WIDTH = 800;

/**
 * Acota un ancho a un rango usable.
 *
 * Sin cota inferior una columna se puede arrastrar hasta cero y después no hay de dónde
 * agarrarla para devolverla; sin cota superior una sola columna empuja al resto fuera de
 * la pantalla. Devuelve null si el valor no es un número, para no persistir NaN.
 */
export function clampWidth(width) {
    const numero = typeof width === 'number' ? width : Number(width);
    if (!Number.isFinite(numero)) return null;
    return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(numero)));
}

/**
 * Combina los anchos guardados por el usuario sobre los anchos por defecto.
 *
 * Manda el conjunto de columnas actual, no el guardado: si una columna se elimina del
 * código, su ancho viejo se descarta en vez de quedar arrastrándose en el navegador de
 * cada usuario para siempre. Un storage corrupto o vacío devuelve los valores por defecto.
 */
export function mergeWidths(defaults, saved) {
    const resultado = { ...defaults };
    if (!saved || typeof saved !== 'object') return resultado;

    for (const key of Object.keys(defaults)) {
        const guardado = clampWidth(saved[key]);
        if (guardado !== null) resultado[key] = guardado;
    }
    return resultado;
}
